/**
 * Screen-local fetch module for the Distribution screen (task 005-08).
 *
 * ADR-010 A2 (b): `lib/api.ts` is never edited by a Stage-C epic — each new
 * screen talks to the api through a module like this one. It re-states the
 * same `API_BASE_URL` expression as the **private** const in `lib/api.ts`
 * (`import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'`), unwraps
 * the same `{ data }` envelope, and maps non-2xx responses to the same
 * `ApiError` imported from `../../lib/api` — so screens keep one error type
 * and screen tests mock this module, not the fetch.
 *
 * Contracts come from `@leads-platform/shared` (task 005-07's response
 * schemas, ADR-010 A3) — no local type mirror.
 */

import {
  type ApiResponse,
  type AttemptKind,
  type AttemptOutcome,
  type DistributionAttemptDetailResponse,
  type DistributionAttemptRowResponse,
  type DlqJobResponse,
} from '@leads-platform/shared';
import { apiFetch, ApiError } from '../../lib/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/**
 * Filters of GET /admin/distribution/attempts — the query params task 005-07's
 * `attemptsQuerySchema` accepts, in the shapes the SPA holds them (ids as
 * strings, dates as yyyy-MM-dd). Empty/undefined entries are omitted from the
 * query string, which is the api's "no filter" spelling for every param
 * (`includeTest` is only sent when the toggle is off — true is the default).
 */
export interface DistributionAttemptsFilters {
  campaignId?: string;
  advertiserId?: string;
  leadId?: string;
  email?: string;
  outcome?: AttemptOutcome;
  kind?: AttemptKind;
  from?: string;
  to?: string;
  includeTest?: boolean;
  limit?: number;
}

/** Map the screen's filters onto the api's query string. */
function attemptsQueryString(filters: DistributionAttemptsFilters): string {
  const params = new URLSearchParams();
  if (filters.campaignId) params.set('campaignId', filters.campaignId);
  if (filters.advertiserId) params.set('advertiserId', filters.advertiserId);
  if (filters.leadId) params.set('leadId', filters.leadId);
  if (filters.email) params.set('email', filters.email);
  if (filters.outcome) params.set('outcome', filters.outcome);
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.includeTest === false) params.set('includeTest', 'false');
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * One request over the admin api: unwrap `{ data }`, map every non-2xx to
 * `ApiError` (the same error type `lib/api.ts` throws — the screen's
 * `.catch((err: ApiError) => …)` idiom keeps working unchanged).
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...init?.headers },
    });
  } catch {
    throw new ApiError(0, 'Network error');
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const message =
      typeof errorData === 'object' &&
      errorData !== null &&
      'message' in errorData &&
      typeof errorData.message === 'string' &&
      errorData.message.length > 0
        ? errorData.message
        : response.statusText;
    throw new ApiError(response.status, message);
  }

  const envelope: ApiResponse<T> = JSON.parse(await response.text());
  return envelope.data;
}

/** GET /admin/distribution/attempts — the filtered attempt log, newest first. */
export function getDistributionAttempts(
  filters: DistributionAttemptsFilters = {}
): Promise<DistributionAttemptRowResponse[]> {
  return request<DistributionAttemptRowResponse[]>(
    `/admin/distribution/attempts${attemptsQueryString(filters)}`
  );
}

/** GET /admin/distribution/attempts/:id — one attempt plus its raw response log. */
export function getDistributionAttempt(id: string): Promise<DistributionAttemptDetailResponse> {
  return request<DistributionAttemptDetailResponse>(`/admin/distribution/attempts/${id}`);
}

/** GET /admin/distribution/dlq — the dead-letter queue, most recently failed first. */
export function getDistributionDlq(): Promise<DlqJobResponse[]> {
  return request<DlqJobResponse[]>('/admin/distribution/dlq');
}

/** POST /admin/distribution/dlq/:jobId/retry — reset the attempt budget and run now. */
export function retryDistributionJob(jobId: string): Promise<DlqJobResponse> {
  return request<DlqJobResponse>(`/admin/distribution/dlq/${jobId}/retry`, { method: 'POST' });
}
